import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import useErrorHandler from '../../services/error-handler';
import { StyledDialogTitle, StyledSortLabel, StyledTableCell } from '../StyledComponents';
import KosaAccountingStore from '../../store/kosa-accounting-store';
import CorrectionDialog from './CorrectionDialog';

//=================================================================================================
const store = new KosaAccountingStore();

//=================================================================================================
const MinQuantityCorrectionDialog = ({ item, onClose }) => {
    const errorHandler = useErrorHandler();

    const [quantity, setQuantity] = useState('');

    const iRef = useRef(null);

    useEffect(() => {
        if (item) {
            const q = item.unit === 'шт.' ? Math.round(item.min_quantity) : item.min_quantity;
            setQuantity(q);
        }
    }, [item]);

    const onQuantityChange = (event) => {
        let input = event.target.value;
        if (!/^[-\d.,]*$/.test(input)) return;

        input = input.replace(',', '.');
        if ((input.match(/-/g) || []).length > 1 || (input.includes('-') && input[0] !== '-')) return;

        if ((input.match(/\./g) || []).length > 1) return;
        setQuantity(input);
    };

    const onEnterPress = (e) => {
        if (e.key === 'Enter') onApplyButtonClick();
    };

    const onApplyButtonClick = async () => {
        try {
            if (isNaN(+quantity)) throw new Error('Указано не возможное количество компонентов');
            await store.setMinQuantity(item, +quantity);
            onClose();
        } catch (e) {
            errorHandler(e);
        }
    };

    if (!item) return null;
    return (
        <Dialog
            open={item}
            onClose={onClose}
            TransitionProps={{ onEntered: () => iRef.current && iRef.current.select() }}
        >
            <StyledDialogTitle>Исправление порога уведомления</StyledDialogTitle>
            <DialogContentText>Порог:</DialogContentText>
            <DialogContent>
                <TextField
                    size="small"
                    fullWidth
                    value={quantity}
                    onChange={onQuantityChange}
                    inputRef={iRef}
                    onKeyDown={onEnterPress}
                    inputProps={{ inputMode: 'decimal', pattern: '[-]?[0-9]*[.,]?[0-9]*' }}
                />
            </DialogContent>
            <DialogActions>
                <Button color="success" variant="contained" onClick={onApplyButtonClick}>
                    Применить
                </Button>
                <Button onClick={onClose} variant="outlined">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};
//=================================================================================================
const Filter = observer(() => {
    const errorHandler = useErrorHandler();

    const onNameFilterChange = async (event) => {
        try {
            const oldFilter = store.filter;
            let value = event.target.value;
            if (!value.length) value = null;
            store.setFilter({ ...oldFilter, name: value });
        } catch (e) {
            errorHandler(e);
        }
    };

    const onTypeFilterChange = async (event) => {
        try {
            const oldFilter = store.filter;
            let value = +event.target.value;
            if (isNaN(value)) value = null;
            store.setFilter({ ...oldFilter, type: value });
        } catch (e) {
            errorHandler(e);
        }
    };

    //-------------------------------------------------------------------------------------------------
    const renderTypeMenu = () => {
        const menu = store.deviceTypes.map((item) => (
            <MenuItem value={item.type_id} key={item.type_id}>
                {item.type}
            </MenuItem>
        ));
        menu.unshift(
            <MenuItem value={null} key={0}>
                Все
            </MenuItem>
        );
        return menu;
    };

    //-------------------------------------------------------------------------------------------------
    return (
        <Stack
            direction="row"
            spacing={2}
            useFlexGap
            justifyContent="center"
            alignItems="center"
            sx={{ flexWrap: 'wrap' }}
        >
            <Box sx={{ m: 'auto' }} />
            <FormControl>
                <InputLabel id="component-type-select" size="small">
                    Тип компонента
                </InputLabel>
                <Select
                    sx={{ minWidth: '10em' }}
                    label="Тип компонента"
                    labelId="component-type-select"
                    variant="outlined"
                    size="small"
                    onChange={onTypeFilterChange}
                >
                    {renderTypeMenu()}
                </Select>
            </FormControl>
            <TextField size="small" label="Наименование" sx={{ width: '10em' }} onChange={onNameFilterChange} />
            <Button color="primary" variant="contained" href="/kosa-components/history">
                История
            </Button>
        </Stack>
    );
});

//=================================================================================================
const LinkButton = styled(Button)({ textTransform: 'none' });

//=================================================================================================
const KosaAccounting = () => {
    const errorHandler = useErrorHandler();

    const [correctionDialog, setCorrectionDialog] = useState(null);
    const [minCorrectionDialog, setMinCorrectionDialog] = useState(null);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            await store.load();
        } catch (e) {
            errorHandler(e);
        }
    };

    //-------------------------------------------------------------------------------------------------
    const renderTableHeader = () => {
        const sortHandler = async (sortColumnName) => {
            try {
                const isAsc = store.sort.column === sortColumnName && store.sort.order === 'asc';
                const sort = {
                    column: sortColumnName,
                    order: isAsc ? 'desc' : 'asc',
                };
                await store.setSort(sort);
            } catch (e) {
                errorHandler(e);
            }
        };

        // prettier-ignore
        const items = [
            { id: 'type_id',     label: 'Тип',                  sort: true  },
            { id: 'name',        label: 'Наименование',         sort: true  },
            { id: 'description', label: 'Описание',             sort: true  },
            { id: 'min_quantity',label: 'Порог предупреждения', sort: false },
            { id: 'quantity',    label: 'Количество',           sort: true  },
            { id: 'unit',        label: 'Единицы',              sort: true  },
            { id: 'actions',     label: '',                     sort: false },
        ];

        const out = [];
        items.forEach((item) => {
            if (item.sort) {
                out.push(
                    <StyledTableCell
                        key={item.id}
                        align="center"
                        sortDirection={store.sort.column === item.id ? store.sort.order : false}
                    >
                        <StyledSortLabel
                            active={store.sort.column === item.id}
                            direction={store.sort.order}
                            onClick={() => sortHandler(item.id)}
                        >
                            {item.label}
                        </StyledSortLabel>
                    </StyledTableCell>
                );
            } else {
                out.push(
                    <StyledTableCell key={item.id} align="center">
                        {item.label}
                    </StyledTableCell>
                );
            }
        });
        return out;
    };

    //-------------------------------------------------------------------------------------------------
    const renderTableBody = () => {
        const style = (item) => {
            const i = { ...item };
            if (parseInt(i.min_quantity) === 0) return null;
            if (parseFloat(i.quantity) < parseFloat(i.min_quantity)) return { backgroundColor: '#FFB9B2' };
            return null;
        };

        return store.items.map((item) => (
            <TableRow key={item.component_id} sx={style(item)}>
                <TableCell align="right">{item.type}</TableCell>
                <TableCell>
                    <Tooltip title="Просмотр истории компонента">
                        <LinkButton href={`kosa-components/history/component/${item.component_id}`}>
                            {item.name}
                        </LinkButton>
                    </Tooltip>
                </TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell align="right">
                    <Tooltip title="Изменить">
                        <LinkButton onClick={() => setMinCorrectionDialog(item)}>
                            {item.unit === 'шт.' ? Math.round(item.min_quantity) : item.min_quantity}
                        </LinkButton>
                    </Tooltip>
                </TableCell>
                <TableCell align="right">
                    <Tooltip title="Корректировать">
                        <LinkButton onClick={() => setCorrectionDialog(item)}>
                            {item.unit === 'шт.' ? Math.round(item.quantity) : item.quantity}
                        </LinkButton>
                    </Tooltip>
                </TableCell>
                <TableCell>{item.unit}</TableCell>
                <TableCell align="right">
                    <Tooltip title="Корректировать">
                        <IconButton onClick={() => setCorrectionDialog(item)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        ));
    };

    return (
        <>
            <Stack spacing={2}>
                <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    Учет компонентов кос
                </Typography>
                <Filter />
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>{renderTableHeader()}</TableRow>
                        </TableHead>
                        <TableBody>{renderTableBody()}</TableBody>
                    </Table>
                </TableContainer>
            </Stack>
            <CorrectionDialog store={store} item={correctionDialog} onClose={() => setCorrectionDialog(null)} />
            <MinQuantityCorrectionDialog item={minCorrectionDialog} onClose={() => setMinCorrectionDialog(null)} />
        </>
    );
};

export default observer(KosaAccounting);
